/**
 * admin_auth — Admin 콘솔 전용 로그인(콕핏 로그인과 별개).
 *
 * ⚠️ 이건 **보안 경계가 아니다.** 정적 SPA 라 검증이 브라우저에서 일어나므로,
 * 번들을 읽거나 개발자도구로 세션 키를 심으면 누구든 우회할 수 있다. 목적은
 * "주소를 아는 사람이 무심코 값을 바꾸는 것"을 막는 것까지다.
 *
 * 진짜로 막으려면 **서버가 막아야 한다**. 다만 지금은 막을 대상 자체가 없다 —
 * saveVehicleMeta 가 아직 스텁이라 이 화면은 무엇도 영구 변경하지 못한다.
 * 저장 API 를 붙이는 순간, 인증은 그 API 쪽에 반드시 들어가야 한다.
 *
 * 비밀번호를 평문으로 두지 않는 이유: 번들을 열어본 사람이 게이트를 우회하는 건
 * 어차피 가능하지만, 다른 곳에 재사용될 수 있는 **비밀번호 문자열 자체를 넘겨주지는
 * 않기** 위해서다.
 */

const SESSION_KEY = "cockpit.adminSession";
const SALT = "aceworks-admin|";

/** 허용된 자격증명의 다이제스트. 평문은 저장소에 남기지 않는다. */
const ALLOWED_DIGEST = "4b9cfb3ed50ff4b3";

/**
 * 의존성 없는 동기 해시.
 *
 * 암호학적으로 강하지 않다 — 이 게이트가 애초에 우회 가능하므로 강한 해시를 쓸
 * 이유가 없고, Web Crypto 는 비동기인 데다 보안 컨텍스트(HTTPS)가 아니면 아예 없어서
 * 평문 HTTP 로 접속하는 내부망에서 잠겨 버린다.
 */
export function digestCredentials(userId: string, password: string): string {
  const input = `${SALT}${userId}:${password}`;

  let a = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    a ^= input.charCodeAt(i);
    a = Math.imul(a, 0x1000193) >>> 0;
  }

  let c = 0x9e3779b9;
  for (let i = input.length - 1; i >= 0; i -= 1) {
    c = Math.imul(c ^ input.charCodeAt(i), 0x85ebca6b) >>> 0;
    c = ((c << 13) | (c >>> 19)) >>> 0;
  }

  return a.toString(16).padStart(8, "0") + c.toString(16).padStart(8, "0");
}

export function isAdminCredentialValid(
  userId: unknown,
  password: unknown,
): boolean {
  if (typeof userId !== "string" || typeof password !== "string") {
    return false;
  }
  if (userId.length === 0 || password.length === 0) {
    return false;
  }
  return digestCredentials(userId, password) === ALLOWED_DIGEST;
}

/**
 * 세션은 sessionStorage 에 둔다 — 탭을 닫으면 사라진다.
 *
 * localStorage 였다면 공용 콕핏 PC 에서 다음 사람이 그대로 admin 으로 들어간다.
 */
export function isAdminSignedIn(): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY) === ALLOWED_DIGEST;
  } catch {
    return false;
  }
}

export function signInAdmin(userId: string, password: string): boolean {
  if (!isAdminCredentialValid(userId, password)) {
    return false;
  }
  try {
    sessionStorage.setItem(SESSION_KEY, ALLOWED_DIGEST);
  } catch {
    /* 저장이 막혀 있어도 이번 세션은 통과시킨다 */
  }
  return true;
}

/**
 * 서버 쓰기에 실을 토큰.
 *
 * 클라이언트 게이트만으로는 API 직접 호출을 막지 못하므로, 저장 요청에는 서버가
 * 검사하는 토큰을 함께 보낸다. 로그인하지 않았으면 null 이고, 서버는 그 요청을 거절한다.
 */
export function getAdminToken(): string | null {
  return isAdminSignedIn() ? ALLOWED_DIGEST : null;
}

export function signOutAdmin(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
}
